import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  cancelRecurringInvoice,
  createRecurringInvoice,
  deleteRecurringInvoicePermanently,
  fetchRecurringInvoices,
  pauseRecurringInvoice,
  resumeRecurringInvoice,
  updateRecurringInvoice,
  type CreateRecurringInvoicePayload,
  type ListFilters,
  type UpdateRecurringInvoicePayload,
} from '../lib/recurring-invoices-api';

export const recurringKeys = {
  all: ['recurring-invoices'] as const,
  lists: () => [...recurringKeys.all, 'list'] as const,
  list: (filters: ListFilters) => [...recurringKeys.lists(), filters] as const,
};

export function useRecurringInvoices(filters: ListFilters = {}) {
  return useQuery({
    queryKey: recurringKeys.list(filters),
    queryFn: () => fetchRecurringInvoices(filters),
    staleTime: 30_000,
  });
}

export function useCreateRecurringInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRecurringInvoicePayload) => createRecurringInvoice(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recurringKeys.all });
    },
  });
}

export function useUpdateRecurringInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRecurringInvoicePayload }) =>
      updateRecurringInvoice(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recurringKeys.all });
    },
  });
}

export function usePauseRecurringInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      pauseRecurringInvoice(id, version),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recurringKeys.all });
    },
  });
}

export function useResumeRecurringInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      resumeRecurringInvoice(id, version),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recurringKeys.all });
    },
  });
}

export function useCancelRecurringInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      cancelRecurringInvoice(id, version),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recurringKeys.all });
    },
  });
}

export function useDeleteRecurringInvoicePermanently() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRecurringInvoicePermanently(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recurringKeys.all });
    },
  });
}
